import { AlertTriangle, Construction, Inbox, RotateCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api.js';
import { cx } from '../../lib/cx.js';
import { errorMessage } from '../../lib/errors.js';
import { Button } from './Button.js';

/** Shimmering block that matches the shape of the content it stands in for. */
export function Skeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return <span className={cx('skeleton block', className)} style={style} aria-hidden />;
}

/** Table skeleton: same row height and column rhythm as the real table. */
export function SkeletonRows({ rows = 8, className }: { rows?: number; className?: string }) {
  return (
    <div className={cx('flex flex-col', className)} role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-[var(--border)] px-3 py-2.5"
          style={{ opacity: 1 - i * 0.07 }}
        >
          <Skeleton className="h-3 w-3 rounded-full" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 flex-1" />
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-10" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonCards({ count = 3, className }: { count?: number; className?: string }) {
  return (
    <div className={cx('grid gap-3', className)} role="status" aria-label="Loading">
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] p-4"
        >
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-3 h-6 w-16" />
          <Skeleton className="mt-3 h-2 w-full" />
        </div>
      ))}
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cx(
        'flex flex-col items-center justify-center gap-2 rounded-[var(--radius-md)] border border-dashed',
        'border-[var(--border-strong)] bg-[var(--surface)] px-6 py-12 text-center',
        className,
      )}
    >
      <span className="text-[var(--text-3)]">{icon ?? <Inbox className="h-6 w-6" />}</span>
      <p className="text-[var(--text-md)] font-medium text-[var(--text)]">{title}</p>
      {description && (
        <p className="max-w-md text-[var(--text-base)] text-[var(--text-2)]">{description}</p>
      )}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export interface ErrorBannerProps {
  title?: string;
  error: unknown;
  onRetry?: () => void;
  /**
   * `unavailable` is the calm treatment for a module the API has not shipped:
   * a 404 on a page's own resource is a missing feature, not a broken one, and
   * must not read as a red alarm. A 501 always gets it.
   */
  variant?: 'error' | 'unavailable';
  className?: string;
}

/** Inline, dismissible-free error with a retry affordance. */
export function ErrorBanner({ title, error, onRetry, variant, className }: ErrorBannerProps) {
  const notImplemented =
    variant === 'unavailable' || (error instanceof ApiError && error.status === 501);
  return (
    <div
      role="alert"
      className={cx(
        'flex items-start gap-2.5 rounded-[var(--radius-md)] border px-3 py-2.5',
        notImplemented
          ? 'border-[var(--border)] bg-[var(--surface-2)]'
          : 'border-[var(--danger)] bg-[var(--danger-soft)]',
        className,
      )}
    >
      <span className={notImplemented ? 'text-[var(--text-3)]' : 'text-[var(--danger)]'}>
        {notImplemented ? (
          <Construction className="mt-0.5 h-4 w-4" />
        ) : (
          <AlertTriangle className="mt-0.5 h-4 w-4" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={cx(
            'text-[var(--text-base)] font-medium',
            notImplemented ? 'text-[var(--text)]' : 'text-[var(--danger)]',
          )}
        >
          {title ?? (notImplemented ? 'Not available yet' : 'Could not load this')}
        </p>
        <p className="text-[var(--text-base)] text-[var(--text-2)]">{errorMessage(error)}</p>
      </div>
      {onRetry && (
        <Button size="sm" variant="secondary" onClick={onRetry}>
          <RotateCw className="h-3.5 w-3.5" />
          Retry
        </Button>
      )}
    </div>
  );
}

/**
 * The three states every data view needs, in one place: skeleton, error banner
 * with retry, or the empty state. Returns null once there is data to show.
 */
export function QueryState({
  isPending,
  error,
  isEmpty,
  onRetry,
  skeleton,
  empty,
  errorTitle,
}: {
  isPending: boolean;
  error: unknown;
  isEmpty?: boolean;
  onRetry?: () => void;
  skeleton?: ReactNode;
  empty?: ReactNode;
  errorTitle?: string;
}) {
  if (isPending) return <>{skeleton ?? <SkeletonRows />}</>;
  if (error)
    return (
      <ErrorBanner
        {...(errorTitle === undefined ? {} : { title: errorTitle })}
        error={error}
        {...(onRetry ? { onRetry } : {})}
      />
    );
  if (isEmpty) return <>{empty ?? <EmptyState title="Nothing here yet" />}</>;
  return null;
}
